import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut, 
  onAuthStateChanged,
  sendPasswordResetEmail,
  updateProfile
} from 'firebase/auth';
import { auth, db } from '../firebase/config';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';

// Create auth context for React's Context API
const AuthContext = createContext();

// Custom hook for easy access to auth context
export function useAuth() {
  return useContext(AuthContext);
}

// Provider component to wrap application with auth functionality
export function AuthProvider({ children }) {
  // State management
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Sign up a new user with email and password
  async function signup(email, password, username) {
    try {
      // Create user with email and password
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Update profile with username
      await updateProfile(user, { displayName: username });
      
      // Create user document in Firestore
      await setDoc(doc(db, 'users', user.uid), {
        uid: user.uid,
        email: email,
        username: username,
        createdAt: serverTimestamp(),
        pollsCreated: [],
        pollsParticipated: []
      });
      
      setError('');
      return user;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }

  // Log in an existing user
  function login(email, password) {
    return signInWithEmailAndPassword(auth, email, password)
      .then(() => {
        setError('');
      })
      .catch(err => {
        setError(err.message);
        throw err;
      });
  }

  // Log out the current user
  function logout() {
    setError('');
    return signOut(auth);
  }

  // Send password reset email
  function resetPassword(email) {
    return sendPasswordResetEmail(auth, email)
      .then(() => {
        setError('');
      })
      .catch(err => {
        setError(err.message);
        throw err;
      });
  }

  // Get additional user data from Firestore
  async function getUserData() {
    if (!currentUser) return null;
    
    try {
      // Reference to user document in Firestore
      const docRef = doc(db, 'users', currentUser.uid);
      const docSnap = await getDoc(docRef);
      
      // Return user data if document exists
      if (docSnap.exists()) {
        return docSnap.data();
      } else {
        return null;
      }
    } catch (err) {
      setError(err.message);
      return null;
    }
  }

  // Listen for authentication state changes
  useEffect(() => {
    // Subscribe to auth state changes
    const unsubscribe = onAuthStateChanged(auth, user => {
      setCurrentUser(user);
      setLoading(false);
    });

    // Unsubscribe on cleanup
    return unsubscribe;
  }, []);

  // Prepare context value with all auth functions
  const value = {
    currentUser,
    login,
    signup,
    logout,
    resetPassword,
    getUserData,
    error,
    setError
  };

  // Only render children when auth state is determined
  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}